import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { SearchIcon, XIcon } from 'lucide-react';
import { categories } from '../../data/categories';
import { creators } from '../../data/creators';
import { products } from '../../data/products';
import { useOverlay } from '../../hooks/useOverlay';
import { getCreator } from '../../utils/catalog';
import { formatPrice } from '../../utils/currency';
import { statusLabel } from '../../utils/exhibition';
import { easeOut } from '../../utils/motion';
import { normalize } from '../../utils/text';
interface SearchOverlayProps {
  open: boolean;
  onClose: () => void;
}
const suggestions = ['céramique', 'lin', 'argent', 'miel', 'cuir', 'bougie'];
export function SearchOverlay({
  open,
  onClose
}: SearchOverlayProps) {
  const [query, setQuery] = useState('');
  useOverlay(open, onClose);
  const q = normalize(query);
  const productResults = useMemo(() => {
    if (!q) return [];
    return products.filter(p => {
      const creator = getCreator(p.creatorId);
      return normalize(`${p.name} ${p.description} ${creator?.name ?? ''} ${creator?.craft ?? ''}`).includes(q);
    }).slice(0, 6);
  }, [q]);
  const creatorResults = useMemo(() => {
    if (!q) return [];
    return creators.filter(c => normalize(`${c.name} ${c.maker} ${c.craft} ${c.city}`).includes(q)).slice(0, 4);
  }, [q]);
  const hasResults = productResults.length + creatorResults.length > 0;
  return <AnimatePresence onExitComplete={() => setQuery('')} data-id="element-470" data-mp="3pec3tfy49tey">
      {open && <>
          <motion.div className="fixed inset-0 z-50 bg-ink/40" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} aria-hidden data-id="element-471" data-mp="3la4a14csxn9v" />
          <motion.div role="dialog" aria-modal="true" aria-label="Recherche" className="fixed inset-x-0 top-0 z-50 max-h-[90vh] overflow-y-auto bg-paper" initial={{
        opacity: 0,
        y: -16
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -16
      }} transition={{
        duration: 0.24,
        ease: easeOut
      }} data-id="element-472" data-mp="24w4e04h2uhrw">
            <div className="mx-auto max-w-5xl px-5 py-6 md:px-8 md:py-10" data-id="element-473" data-mp="21e6yisyh9qgu">
              <div className="flex items-center gap-3 border-b border-ink pb-3" data-id="element-474" data-mp="1nnbt5qn6tri0">
                <SearchIcon className="h-5 w-5 shrink-0 text-muted" aria-hidden data-id="element-475" data-mp="1sfldmr5j9zcw" />
                <label htmlFor="site-search" className="sr-only" data-id="element-476" data-mp="1dqgxdfiau57f">
                  Rechercher
                </label>
                <input id="site-search" autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder="Une pièce, un créateur, une matière…" className="min-w-0 flex-1 bg-transparent font-display text-2xl outline-none placeholder:text-muted/60 md:text-4xl" data-id="element-477" data-mp="1ioih82nju8jy" />
                <button type="button" onClick={onClose} aria-label="Fermer la recherche" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-shell" data-id="element-478" data-mp="l4il6nz1vg1t">
                  <XIcon className="h-5 w-5" data-id="element-479" data-mp="191o7u2590gxk" />
                </button>
              </div>

              {!q && <div className="mt-8 grid gap-8 md:grid-cols-2" data-id="element-480" data-mp="14337m2pzxx94">
                  <div data-id="element-481" data-mp="140gyvzrddbpb">
                    <p className="mb-3 text-xs font-medium text-muted" data-id="element-482" data-mp="1ir7s9c8m0iuj">Recherches fréquentes</p>
                    <div className="flex flex-wrap gap-2" data-id="element-483" data-mp="1dsmstf16m375">
                      {suggestions.map(s => <button key={s} type="button" onClick={() => setQuery(s)} className="h-9 rounded-full border border-line px-4 text-sm transition-colors duration-150 hover:border-ink" data-id="element-484" data-mp="1rrmfih54w80o">
                          {s}
                        </button>)}
                    </div>
                  </div>
                  <div data-id="element-485" data-mp="3r179z2xirtlm">
                    <p className="mb-3 text-xs font-medium text-muted" data-id="element-486" data-mp="3v0oe9t17mkp7">Univers</p>
                    <ul className="space-y-2" data-id="element-487" data-mp="3v2skwiqhtxt6">
                      {categories.map(c => <li key={c.id} data-id="element-488" data-mp="2yci831on3k8t">
                          <Link to={`/boutique?categorie=${c.id}`} className="text-sm hover:text-clay" data-id="element-489" data-mp="33909brkrh0ge">
                            {c.name}
                          </Link>
                        </li>)}
                    </ul>
                  </div>
                </div>}

              {q && !hasResults && <p className="mt-8 text-muted" data-id="element-490" data-mp="mkmijfna9rts">
                  Aucun résultat pour « {query} ». Essayez une matière comme « lin » ou « grès ».
                </p>}

              {q && hasResults && <div className="mt-8 grid gap-10 md:grid-cols-[1fr_280px]" data-id="element-491" data-mp="wot96nnjl5sy">
                  <div data-id="element-492" data-mp="wpl2tsxkwtgl">
                    <p className="mb-3 text-xs font-medium text-muted" data-id="element-493" data-mp="ry33187akyx7">Pièces ({productResults.length})</p>
                    <ul className="grid gap-3 sm:grid-cols-2" data-id="element-494" data-mp="nwraoux19zlb">
                      {productResults.map(p => <li key={p.id} data-id="element-495" data-mp="ixohc7qf1ht6">
                          <Link to={`/produit/${p.id}`} className="group flex items-center gap-3 rounded-sm p-1.5 hover:bg-shell" data-id="element-496" data-mp="iypsec7fm4aw">
                            <img src={p.images[0]} alt="" className="h-16 w-14 shrink-0 rounded-sm object-cover" data-id="element-497" data-mp="8z8q84re28jo" />
                            <span className="min-w-0" data-id="element-498" data-mp="4y59zh5fiahx">
                              <span className="block truncate text-sm font-medium" data-id="element-499" data-mp="4zgg3nmcjuc7">{p.name}</span>
                              <span className="block text-xs text-muted" data-id="element-500" data-mp="23cgmm0v0is7f">
                                {getCreator(p.creatorId)?.name} · {formatPrice(p.price)}
                              </span>
                            </span>
                          </Link>
                        </li>)}
                    </ul>
                  </div>
                  {creatorResults.length > 0 && <div data-id="element-501" data-mp="3jrvxxrsf3bwi">
                      <p className="mb-3 text-xs font-medium text-muted" data-id="element-502" data-mp="3nusjnrd97o4o">Créateurs</p>
                      <ul className="space-y-3" data-id="element-503" data-mp="ffqo7tew8rzn">
                        {creatorResults.map(c => <li key={c.id} data-id="element-504" data-mp="kebotty25dom">
                            <Link to={`/createurs/${c.id}`} className="block hover:text-clay" data-id="element-505" data-mp="ke1y2456v2hv">
                              <span className="font-display text-xl" data-id="element-506" data-mp="6e0xyqwqzz3g">{c.name}</span>
                              <span className="block text-xs text-muted" data-id="element-507" data-mp="bcudeovli069">{statusLabel(c)}</span>
                            </Link>
                          </li>)}
                      </ul>
                    </div>}
                </div>}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}